import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Pencil, Target, Trash2 } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../authentication/AuthContext';
import AddTrackerModal from './components/AddTrackerModal';
import { useCreateTrackerEntry, useDeleteTracker, useTracker, useTrackerEntries, useUpdateTracker } from './hooks/useTrackers';

function formatDate(timestamp) {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function startOfToday() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.getTime();
}

function isSuccess(tracker, entry) {
    if (!entry) return false;
    if (tracker.type === 'BOOLEAN') return Boolean(entry.completed);
    const targetValue = Number(tracker.targetValue ?? tracker.numericTarget ?? 0);
    const entryValue = Number(entry.numericValue ?? 0);
    return entryValue >= targetValue;
}

function EntryCard({ tracker, entry }) {
    const success = isSuccess(tracker, entry);

    return (
        <div className="rounded-xl border border-[#2A2B32] bg-[#1E1F25] p-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-medium text-gray-100">{formatDate(entry.loggedDate)}</p>
                    <p className="mt-1 text-xs text-gray-500">
                        {tracker.type === 'BOOLEAN'
                            ? (entry.completed ? 'Completed' : 'Not completed')
                            : `Value: ${entry.numericValue ?? '—'}${tracker.targetUnit || tracker.unit ? ` ${tracker.targetUnit || tracker.unit}` : ''}`}
                    </p>
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${success ? 'border-green-500/20 bg-green-500/10 text-green-300' : 'border-amber-500/20 bg-amber-500/10 text-amber-300'}`}>
                    {success ? 'Success' : 'Pending'}
                </span>
            </div>
            {entry.note && (
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{entry.note}</p>
            )}
        </div>
    );
}

export default function TrackerDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authToken } = useAuth();
    const { data: tracker, isLoading, refetch } = useTracker(authToken, id);
    const { data: entries = [] } = useTrackerEntries(authToken, id);
    const { mutate: updateTracker, isLoading: isUpdating } = useUpdateTracker(authToken, id);
    const { mutate: deleteTracker, isLoading: isDeleting } = useDeleteTracker(authToken);
    const { mutate: saveEntry, isLoading: isSavingEntry } = useCreateTrackerEntry(authToken, id);
    const [showEditModal, setShowEditModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [entryForm, setEntryForm] = useState({
        numericValue: '',
        completed: false,
        note: '',
    });

    const todayEntry = useMemo(() => {
        const today = startOfToday();
        return entries.find((entry) => Number(entry.loggedDate) === today) ?? null;
    }, [entries]);

    useEffect(() => {
        if (!todayEntry) {
            setEntryForm({
                numericValue: '',
                completed: false,
                note: '',
            });
            return;
        }

        setEntryForm({
            numericValue: todayEntry.numericValue ?? '',
            completed: Boolean(todayEntry.completed),
            note: todayEntry.note ?? '',
        });
    }, [todayEntry]);

    const sortedEntries = useMemo(
        () => [...entries].sort((a, b) => Number(b.loggedDate ?? 0) - Number(a.loggedDate ?? 0)),
        [entries]
    );

    const handleSaveEntry = () => {
        const payload = {
            logged_date: startOfToday(),
            note: entryForm.note.trim(),
        };

        if (tracker.type === 'BOOLEAN') {
            payload.completed = Boolean(entryForm.completed);
        } else {
            payload.numeric_value = entryForm.numericValue === '' ? null : Number(entryForm.numericValue);
        }

        saveEntry(payload, {
            onSuccess: () => {
                refetch();
            },
        });
    };

    const handleDelete = () => {
        deleteTracker(id, {
            onSuccess: () => navigate('/trackers'),
        });
    };

    const handleEditSubmit = (trackerData) => {
        updateTracker(trackerData, {
            onSuccess: () => {
                setShowEditModal(false);
                refetch();
            },
        });
    };

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#12131a]">
                <div className="text-sm text-gray-400">Loading...</div>
            </div>
        );
    }

    if (!tracker) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#12131a]">
                <div className="text-sm text-gray-500">Tracker not found.</div>
            </div>
        );
    }

    const editInitialData = {
        title: tracker.title,
        description: tracker.description,
        type: tracker.type,
        targetValue: tracker.targetValue ?? tracker.numericTarget ?? '',
        targetUnit: tracker.targetUnit ?? tracker.unit ?? '',
        scheduleType: tracker.scheduleType ?? tracker.schedule_type ?? 'DAILY',
        score: tracker.score ?? '',
    };

    return (
        <div className="min-h-screen bg-[#12131a]">
            <Header />
            <div className="mx-auto max-w-3xl px-6 pb-16 pt-24">
                <button
                    type="button"
                    onClick={() => navigate('/trackers')}
                    className="mb-8 flex items-center gap-2 text-sm text-gray-400 transition hover:text-gray-200"
                >
                    <ArrowLeft size={16} />
                    Back to trackers
                </button>

                <div className="mb-4 rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-6 shadow-lg">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-300">
                                <Target size={20} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-semibold text-gray-100">{tracker.title}</h1>
                                <p className="mt-1 text-sm text-gray-500">
                                    {tracker.type === 'BOOLEAN' ? 'Boolean tracker' : 'Numeric tracker'}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-2">
                            <span className="rounded-full border border-indigo-500/20 bg-indigo-500/10 px-3 py-1 text-xs font-medium text-indigo-300">
                                {tracker.type === 'BOOLEAN'
                                    ? 'Yes / No'
                                    : `≥ ${tracker.targetValue ?? tracker.numericTarget ?? 0}${tracker.targetUnit || tracker.unit ? ` ${tracker.targetUnit || tracker.unit}` : ''}`}
                            </span>
                            <span className="rounded-full border border-[#3A4257] bg-[#171A21] px-3 py-1 text-xs font-medium text-gray-300">
                                Score: {tracker.score ?? '—'}
                            </span>
                        </div>
                    </div>
                    {tracker.description && (
                        <p className="mt-3 text-sm leading-relaxed text-gray-400">{tracker.description}</p>
                    )}
                </div>

                <div className="mb-6">
                    <div className="rounded-xl border border-[#2A2B32] bg-[#1E1F25] p-4">
                        <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-500">
                            <Calendar size={12} />
                            Today&apos;s entry
                        </div>

                        {tracker.type === 'BOOLEAN' ? (
                            <label className="flex items-center gap-3 rounded-xl border border-[#2A2B32] bg-[#171A21] px-4 py-3 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={entryForm.completed}
                                    onChange={(event) => setEntryForm((prev) => ({ ...prev, completed: event.target.checked }))}
                                    className="h-4 w-4 accent-indigo-500"
                                />
                                Completed today
                            </label>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <label htmlFor="tracker-entry-value" className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Value
                                </label>
                                <input
                                    id="tracker-entry-value"
                                    type="number"
                                    min="0"
                                    value={entryForm.numericValue}
                                    onChange={(event) => setEntryForm((prev) => ({ ...prev, numericValue: event.target.value }))}
                                    className="w-full rounded-lg bg-[#2A2B32] px-3 py-2 text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g. 50"
                                />
                            </div>
                        )}

                        <div className="mt-3">
                            <label htmlFor="tracker-entry-note" className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                                Note
                            </label>
                            <textarea
                                id="tracker-entry-note"
                                rows={3}
                                value={entryForm.note}
                                onChange={(event) => setEntryForm((prev) => ({ ...prev, note: event.target.value }))}
                                className="w-full resize-none rounded-lg bg-[#2A2B32] px-3 py-2 text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Optional note"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={handleSaveEntry}
                            disabled={isSavingEntry}
                            className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {todayEntry ? "Update today's entry" : "Save today's entry"}
                        </button>
                    </div>
                </div>

                <div className="rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-100">Entries</h2>

                    {sortedEntries.length > 0 ? (
                        <div className="grid gap-3">
                            {sortedEntries.map((entry) => (
                                <EntryCard key={entry.id} tracker={tracker} entry={entry} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No entries yet.</p>
                    )}
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => setShowEditModal(true)}
                        disabled={isUpdating}
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-300 transition hover:border-indigo-500/50 hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <Pencil size={15} />
                        Edit
                    </button>

                    {!confirmDelete ? (
                        <button
                            type="button"
                            onClick={() => setConfirmDelete(true)}
                            className="inline-flex items-center gap-2 rounded-xl border border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-400 transition hover:border-red-500/50 hover:text-red-400"
                        >
                            <Trash2 size={15} />
                            Delete
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                Confirm delete
                            </button>
                            <button
                                type="button"
                                onClick={() => setConfirmDelete(false)}
                                className="rounded-xl border border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-400 transition hover:bg-gray-800"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {showEditModal && (
                <AddTrackerModal
                    isEditMode
                    isSubmitting={isUpdating}
                    initialData={editInitialData}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={handleEditSubmit}
                />
            )}
        </div>
    );
}
